import type { AttestatoAdmin, AttestatoMio, Report, VerificaAttestato } from '@aulaviva/shared';
import type { z } from 'zod';
import type { riepilogoAttestatiSchema } from '@aulaviva/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Paginato } from './admin';
import { richiesta } from './client';

export type RiepilogoAttestati = z.infer<typeof riepilogoAttestatiSchema>;

export const chiaviDocumenti = {
  miei: ['documenti', 'attestati-miei'] as const,
  verifica: (codice: string) => ['documenti', 'verifica', codice] as const,
  admin: (webinarId: string, q: string, stato: string, pagina: number) => ['documenti', 'attestati-admin', webinarId, q, stato, pagina] as const,
  riepilogo: (webinarId: string) => ['documenti', 'attestati-riepilogo', webinarId] as const,
  report: (webinarId: string) => ['documenti', 'report', webinarId] as const,
};

// ---- partecipante
export function useMieiAttestati(intervalloMs = 30_000) {
  return useQuery({
    queryKey: chiaviDocumenti.miei,
    queryFn: async () => (await richiesta<AttestatoMio[]>('/me/attestati')).dati,
    // finché un attestato è in preparazione, si ricontrolla
    refetchInterval: (q) => (q.state.data?.some((a) => a.stato === 'in_coda') ? intervalloMs : false),
  });
}

// ---- pubblica
export function useVerificaAttestato(codice: string | null) {
  return useQuery({
    queryKey: chiaviDocumenti.verifica(codice ?? ''),
    queryFn: async () => (await richiesta<VerificaAttestato>(`/verifica/${encodeURIComponent(codice ?? '')}`)).dati,
    enabled: Boolean(codice),
    retry: false,
  });
}

// ---- admin
export function useAttestatiAdmin(webinarId: string, q: string, stato: string, pagina: number, perPagina = 50, intervalloMs = 10_000) {
  const p = new URLSearchParams({ pagina: String(pagina), perPagina: String(perPagina) });
  if (q) p.set('q', q);
  if (stato) p.set('stato', stato);
  return useQuery({
    queryKey: chiaviDocumenti.admin(webinarId, q, stato, pagina),
    queryFn: async () => (await richiesta<Paginato<AttestatoAdmin>>(`/admin/webinar/${webinarId}/attestati?${p.toString()}`)).dati,
    refetchInterval: (query) => (query.state.data?.elementi.some((a) => a.stato === 'in_coda') ? intervalloMs : false),
    placeholderData: (prec) => prec,
  });
}
export function useRiepilogoAttestati(webinarId: string, abilitato = true, intervalloMs = 10_000) {
  return useQuery({
    queryKey: chiaviDocumenti.riepilogo(webinarId),
    queryFn: async () => (await richiesta<RiepilogoAttestati>(`/admin/webinar/${webinarId}/attestati/riepilogo`)).dati,
    enabled: abilitato,
    refetchInterval: (q) => ((q.state.data?.inCoda ?? 0) > 0 ? intervalloMs : false),
  });
}
function useInvalidaAttestati(webinarId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['documenti', 'attestati-admin', webinarId] });
    void qc.invalidateQueries({ queryKey: chiaviDocumenti.riepilogo(webinarId) });
  };
}
export function useRevocaAttestato(webinarId: string) {
  const invalida = useInvalidaAttestati(webinarId);
  return useMutation({
    mutationFn: async (input: { attestatoId: string; motivo: string }) => (await richiesta<AttestatoAdmin>(`/admin/attestati/${input.attestatoId}/revoca`, { method: 'POST', json: { motivo: input.motivo } })).dati,
    onSuccess: invalida,
  });
}
export function useRigeneraAttestato(webinarId: string) {
  const invalida = useInvalidaAttestati(webinarId);
  return useMutation({
    mutationFn: async (attestatoId: string) => (await richiesta<AttestatoAdmin>(`/admin/attestati/${attestatoId}/rigenera`, { method: 'POST' })).dati,
    onSuccess: invalida,
  });
}
export function useGeneraMancanti(webinarId: string) {
  const invalida = useInvalidaAttestati(webinarId);
  return useMutation({
    mutationFn: async () => (await richiesta<{ accodati: number }>(`/admin/webinar/${webinarId}/attestati/genera-mancanti`, { method: 'POST' })).dati,
    onSuccess: invalida,
  });
}
export function useReportAdmin(webinarId: string, intervalloMs = 10_000) {
  return useQuery({
    queryKey: chiaviDocumenti.report(webinarId),
    queryFn: async () => (await richiesta<Report[]>(`/admin/webinar/${webinarId}/report`)).dati,
    refetchInterval: (q) => (q.state.data?.some((r) => r.stato === 'in_coda') ? intervalloMs : false),
  });
}
export function useGeneraReport(webinarId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await richiesta<Report>(`/admin/webinar/${webinarId}/report/genera`, { method: 'POST' })).dati,
    onSuccess: () => void qc.invalidateQueries({ queryKey: chiaviDocumenti.report(webinarId) }),
  });
}
